import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (guideRef.current && !guideRef.current.contains(event.target as Node)) {
        setGuideOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(event.target as Node) && !(event.target as Element).closest('button[aria-label="Toggle menu"]')) {
        setMobileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/90 backdrop-blur-md border-b border-background-200/70" ref={mobileRef}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-table-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-lg font-semibold text-foreground-950 whitespace-nowrap">
              Sugargoo <span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-3 py-2 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line mr-1.5"></i>
              Home
            </Link>

            {/* Shopping Guide Dropdown */}
            <div className="relative" ref={guideRef}>
              <button
                onClick={() => setGuideOpen(!guideOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line mr-1.5"></i>
                Shopping Guides
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden z-50 shadow-sm">
                  <div className="p-2">
                    <div className="px-3 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">SEO Guides</div>
                    {articles.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/` + article.slug}
                        onClick={() => setGuideOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent-50 transition-colors cursor-pointer"
                      >
                        <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                          <i className="ri-article-line text-accent-500 text-sm"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm text-foreground-800 font-medium truncate">{article.title.length > 45 ? article.title.slice(0, 45) + '...' : article.title}</div>
                          <div className="text-xs text-foreground-500">{article.readTime}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/sugargoo-spreadsheet-guide"
              className="px-3 py-2 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-guide-line mr-1.5"></i>
              Complete Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-3 py-2 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-store-2-line mr-1.5"></i>
              Shop Now
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 inline-flex items-center px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-foreground-800 text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50">
          <div className="px-4 py-4 space-y-1 max-h-[70vh] overflow-y-auto">

            {/* Home */}
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary-50 transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                <i className="ri-home-4-line text-primary-500"></i>
              </div>
              <span className="text-sm font-medium text-foreground-800">Home</span>
            </Link>

            {/* Mobile Category Links */}
            <div className="pt-2 pb-1">
              <div className="px-3 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                Product Categories
              </div>
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent-50 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} text-accent-500 text-sm`}></i>
                  </div>
                  <span className="text-sm text-foreground-700 font-medium">{cat.name}</span>
                  <i className="ri-external-link-line text-foreground-400 text-xs ml-auto"></i>
                </a>
              ))}
            </div>

            {/* Mobile Guide Links */}
            <div className="pt-2 pb-1">
              <div className="px-3 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                Shopping Guides
              </div>
              <Link
                to="/sugargoo-spreadsheet-guide"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                  <i className="ri-guide-line text-primary-500 text-sm"></i>
                </div>
                <span className="text-sm font-medium text-foreground-800">Complete Guide</span>
              </Link>
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/` + article.slug}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent-50 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-article-line text-accent-500 text-sm"></i>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-foreground-700 font-medium truncate">{article.title.length > 40 ? article.title.slice(0, 40) + '...' : article.title}</div>
                    <div className="text-xs text-foreground-500">{article.readTime}</div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Mobile External Links */}
            <div className="pt-2 pb-1">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                  <i className="ri-store-2-line text-primary-500 text-sm"></i>
                </div>
                <span className="text-sm font-medium text-foreground-800">Shop Now</span>
                <i className="ri-external-link-line text-foreground-400 text-xs ml-auto"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer mt-2"
              >
                <div className="w-8 h-8 rounded-lg bg-background-50/20 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line text-background-50 text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-background-50">Buy It Now</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}